import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "@/integrations/aws/data";
import { useConfirm } from "@/components/confirm-dialog";
import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, Copy, Layers, Pencil, History, AlertCircle, RotateCcw } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { BILLING_FREQ, FREQ_LABEL, type BillingFrequency } from "./services";
import { resolveCompanyPriceList } from "@/lib/billing";
import { CompanySelect } from "@/components/entity-select";
import type { ResolvedService } from "@/integrations/aws/api";
import {
  FEE_SOURCE_LABEL,
  PRICING_MODE_HINT,
  PRICING_MODE_LABEL,
  asPricingMode,
  ownsItsFees,
  usesGroupSchedule,
} from "@/lib/pricing";

export const Route = createFileRoute("/_authenticated/companies_/$companyId/services")({
  head: () => ({
    meta: [
      { title: "Company Service Plan — Somasi Ops" },
      { name: "description", content: "Assign services and set fees for this company." },
    ],
  }),
  component: CompanyServicesPage,
});

type Status = "active" | "paused" | "discontinued";

type CompanyService = {
  id: string;
  company_id: string;
  service_id: string;
  start_date: string | null;
  end_date: string | null;
  billing_frequency: BillingFrequency | null;
  /** `null` means "inherit" — see `pricing_mode`. */
  fee: number | null;
  next_billing_date: string | null;
  invoice_description: string | null;
  recurring: boolean;
  auto_suggest: boolean;
  approval_required: boolean;
  status: Status;
  notes: string | null;
};

type Company = {
  id: string;
  legal_name: string;
  client_group_id: string | null;
  pricing_mode: "somasi_standard" | "client_group" | "company_specific" | "manual";
  fee_schedule_id: string | null;
};

type Override = {
  id: string;
  service_id: string | null;
  original_fee: number;
  revised_fee: number;
  reason: string;
  reason_notes: string | null;
  changed_at: string;
  approval_status: string;
};

const OVERRIDE_REASONS = [
  "special_client_agreement",
  "discount_approved",
  "additional_work",
  "urgent_service",
  "fee_waiver",
  "partial_service",
  "prior_billing_adjustment",
  "management_instruction",
  "correction",
  "other",
] as const;
const REASON_LABEL: Record<string, string> = {
  special_client_agreement: "Special client agreement",
  discount_approved: "Discount approved",
  additional_work: "Additional work performed",
  urgent_service: "Urgent service",
  fee_waiver: "Fee waiver",
  partial_service: "Partial service",
  prior_billing_adjustment: "Prior billing adjustment",
  management_instruction: "Management instruction",
  correction: "Correction",
  other: "Other",
};

function CompanyServicesPage() {
  const { companyId } = useParams({ from: "/_authenticated/companies_/$companyId/services" });
  const { canEdit, user } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: company } = useQuery({
    queryKey: ["company", companyId],
    queryFn: async () => {
      const { data, error } = await api.companies.get(companyId, [
        "id",
        "legal_name",
        "client_group_id",
        "pricing_mode",
        "fee_schedule_id",
      ]);
      if (error) throw error;
      return data as Company;
    },
  });

  const { data: services = [] } = useQuery({
    queryKey: ["services_active"],
    queryFn: async () =>
      (
        await api.services
          .list()
          .all()
          .select("id, code, name, default_fee, billing_frequency, invoice_description, active")
          .eq("active", true)
          .order("code")
      ).data ?? [],
  });

  // The cascade lives in one place — the price-list endpoint — so the fee shown
  // here is the fee an invoice will actually be raised at. Resolving it a second
  // time in the browser is how the two drifted apart before.
  const mode = asPricingMode(company?.pricing_mode, company ?? undefined);
  const editableFees = ownsItsFees(mode);

  const { data: priceList = [] } = useQuery({
    queryKey: ["price_list", companyId],
    enabled: !!company,
    queryFn: () => resolveCompanyPriceList(companyId),
  });

  const resolvedByService = useMemo(() => {
    const m = new Map<string, ResolvedService>();
    priceList.forEach((p) => m.set(p.service_id, p));
    return m;
  }, [priceList]);

  const { data: assigned = [] } = useQuery({
    queryKey: ["company_services", companyId],
    queryFn: async () =>
      ((await db.from("company_services").select("*").eq("company_id", companyId))
        .data as CompanyService[]) ?? [],
  });

  const { data: overrides = [] } = useQuery({
    queryKey: ["fee_overrides", companyId],
    queryFn: async () =>
      ((
        await db
          .from("invoice_fee_overrides")
          .select("*")
          .eq("company_id", companyId)
          .order("changed_at", { ascending: false })
      ).data as Override[]) ?? [],
  });

  const assignedByServiceId = useMemo(() => {
    const m = new Map<string, CompanyService>();
    assigned.forEach((a) => m.set(a.service_id, a));
    return m;
  }, [assigned]);

  const [copyOpen, setCopyOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [overrideFor, setOverrideFor] = useState<CompanyService | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const toggle = async (serviceId: string, on: boolean) => {
    if (!canEdit || !company) return;
    if (on) {
      const svc = services.find((s) => s.id === serviceId);
      const resolved = resolvedByService.get(serviceId);
      const freq =
        (resolved?.billing_frequency as BillingFrequency | null) ??
        (svc?.billing_frequency as BillingFrequency | undefined) ??
        "annual";
      // Under an inherited mode the fee belongs to a level above, so the plan row
      // stores none and the resolver answers for it on every read.
      const { error } = await db.from("company_services").insert({
        company_id: companyId,
        service_id: serviceId,
        fee: editableFees ? (resolved?.fee ?? Number(svc?.default_fee ?? 0)) : null,
        billing_frequency: freq,
        invoice_description: svc?.invoice_description ?? null,
        recurring: freq !== "one_time",
        status: "active",
      } as never);
      if (error) return notify.error(error);
    } else {
      const existing = assignedByServiceId.get(serviceId);
      if (!existing) return;
      if (
        !(await confirm({
          title: "Remove this service from the plan?",
          description:
            "The company stops being billed for it. Its price stays on whatever schedule sets it.",
          confirmLabel: "Remove",
          tone: "warning",
        }))
      )
        return;
      const { error } = await db.from("company_services").delete().eq("id", existing.id);
      if (error) return notify.error(error);
    }
    qc.invalidateQueries({ queryKey: ["company_services", companyId] });
    qc.invalidateQueries({ queryKey: ["price_list", companyId] });
  };

  const updateAssigned = async (row: CompanyService, patch: Partial<CompanyService>) => {
    const { error } = await db
      .from("company_services")
      .update(patch as never)
      .eq("id", row.id);
    if (error) return notify.error(error);
    qc.invalidateQueries({ queryKey: ["company_services", companyId] });
    qc.invalidateQueries({ queryKey: ["price_list", companyId] });
  };

  const submitOverride = async (revisedFee: number, reason: string, notes: string) => {
    if (!overrideFor || !user) return;
    const original = Number(
      overrideFor.fee ?? resolvedByService.get(overrideFor.service_id)?.fee ?? 0,
    );
    const { error: e1 } = await db.from("invoice_fee_overrides").insert({
      company_service_id: overrideFor.id,
      company_id: companyId,
      service_id: overrideFor.service_id,
      original_fee: original,
      revised_fee: revisedFee,
      reason,
      reason_notes: notes || null,
      changed_by: user.id,
    } as never);
    if (e1) return notify.error(e1);
    const { error: e2 } = await db
      .from("company_services")
      .update({ fee: revisedFee } as never)
      .eq("id", overrideFor.id);
    if (e2) return notify.error(e2);
    notify.success("Fee override recorded");
    setOverrideFor(null);
    qc.invalidateQueries({ queryKey: ["company_services", companyId] });
    qc.invalidateQueries({ queryKey: ["price_list", companyId] });
    qc.invalidateQueries({ queryKey: ["fee_overrides", companyId] });
  };

  if (!company) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div>
      <PageHeader
        title={`Service Plan · ${company.legal_name}`}
        description="Tick services to include them, adjust fee and frequency inline. Fees pre-fill from the applicable schedule."
        breadcrumbs={[
          { label: "Companies", to: "/companies" },
          { label: company.legal_name },
          { label: "Service plan" },
        ]}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to="/companies">
                <ArrowLeft className="mr-2 h-4 w-4" /> Companies
              </Link>
            </Button>
            <Button variant="outline" onClick={() => setHistoryOpen(true)}>
              <History className="mr-2 h-4 w-4" /> Overrides ({overrides.length})
            </Button>
            {canEdit && (
              <Button variant="outline" onClick={() => setCopyOpen(true)}>
                <Copy className="mr-2 h-4 w-4" /> Copy from company
              </Button>
            )}
            {canEdit && company.client_group_id && (
              <Button variant="outline" onClick={() => setBulkOpen(true)}>
                <Layers className="mr-2 h-4 w-4" /> Apply to group
              </Button>
            )}
          </div>
        }
      />

      <Alert className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Fee schedule pricing: {PRICING_MODE_LABEL[mode]}</AlertTitle>
        <AlertDescription className="space-y-1">
          <p>{PRICING_MODE_HINT[mode]}</p>
          {!editableFees && (
            <p>
              To change what this company pays, either reprice the level it follows
              {usesGroupSchedule(mode) && company.client_group_id ? (
                <>
                  {" — "}
                  <Link
                    to="/client-groups/$groupId/pricing"
                    params={{ groupId: company.client_group_id }}
                    className="underline underline-offset-2"
                  >
                    its group pricing
                  </Link>
                </>
              ) : (
                <>
                  {" — "}
                  <Link to="/services" className="underline underline-offset-2">
                    the service catalogue
                  </Link>
                </>
              )}
              {" — or switch it to "}
              <strong>{PRICING_MODE_LABEL.company_specific}</strong> on the company record.
            </p>
          )}
        </AlertDescription>
      </Alert>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12" />
              <TableHead>Service</TableHead>
              <TableHead className="w-32">Frequency</TableHead>
              <TableHead className="w-44 text-right">Fee</TableHead>
              <TableHead className="w-28">Next billing</TableHead>
              <TableHead className="w-20">Recurring</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.map((s) => {
              const row = assignedByServiceId.get(s.id);
              const on = !!row;
              const resolved = resolvedByService.get(s.id);
              return (
                <TableRow key={s.id} className={on ? "" : "opacity-70"}>
                  <TableCell>
                    <Checkbox
                      checked={on}
                      disabled={!canEdit}
                      onCheckedChange={(v) => toggle(s.id, !!v)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {s.code}
                      {resolved && !on && (
                        <>
                          {" "}
                          · USD {resolved.fee.toFixed(2)} (
                          {FEE_SOURCE_LABEL[resolved.source].toLowerCase()})
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row ? (
                      <Select
                        value={row.billing_frequency ?? "annual"}
                        onValueChange={(v) =>
                          updateAssigned(row, { billing_frequency: v as BillingFrequency })
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BILLING_FREQ.map((f) => (
                            <SelectItem key={f} value={f}>
                              {FREQ_LABEL[f]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {FREQ_LABEL[s.billing_frequency as BillingFrequency]}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {row ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-sm">
                            USD {Number(resolved?.fee ?? row.fee ?? 0).toFixed(2)}
                          </span>
                          {canEdit && editableFees && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => setOverrideFor(row)}
                              title="Change fee (audited)"
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                          {canEdit && editableFees && row.fee != null && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              title="Drop this company's own fee and inherit again"
                              onClick={() => updateAssigned(row, { fee: null })}
                            >
                              <RotateCcw className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        {resolved && (
                          <Badge
                            variant={resolved.source === "company fee" ? "default" : "outline"}
                            className="text-[9px] font-normal"
                          >
                            {FEE_SOURCE_LABEL[resolved.source]}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="font-mono text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row ? (
                      <Input
                        type="date"
                        className="h-8 text-xs"
                        value={row.next_billing_date ?? ""}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateAssigned(row, { next_billing_date: e.target.value || null })
                        }
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row && (
                      <Switch
                        checked={row.recurring}
                        disabled={!canEdit}
                        onCheckedChange={(v) => updateAssigned(row, { recurring: v })}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {row ? (
                      <Select
                        value={row.status}
                        onValueChange={(v) => updateAssigned(row, { status: v as Status })}
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="paused">Paused</SelectItem>
                          <SelectItem value="discontinued">Discontinued</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">
                        not in plan
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <CopyDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        companyId={companyId}
        onDone={() => qc.invalidateQueries({ queryKey: ["company_services", companyId] })}
      />
      <BulkApplyDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        clientGroupId={company.client_group_id}
        sourceCompanyId={companyId}
      />
      <OverrideDialog
        row={overrideFor}
        onClose={() => setOverrideFor(null)}
        onSubmit={submitOverride}
      />
      <HistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        overrides={overrides}
        services={services}
      />
    </div>
  );
}

function CopyDialog({
  open,
  onOpenChange,
  companyId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  companyId: string;
  onDone: () => void;
}) {
  const [sourceId, setSourceId] = useState<string>("");
  const [overwrite, setOverwrite] = useState(false);
  const run = async () => {
    if (!sourceId) return;
    const { data: src } = await db.from("company_services").select("*").eq("company_id", sourceId);
    if (!src?.length) return notify.error("Source company has no services to copy");
    if (overwrite) await db.from("company_services").delete().eq("company_id", companyId);
    const payload = src.map((s) => ({
      company_id: companyId,
      service_id: s.service_id,
      billing_frequency: s.billing_frequency,
      fee: s.fee,
      invoice_description: s.invoice_description,
      recurring: s.recurring,
      auto_suggest: s.auto_suggest,
      approval_required: s.approval_required,
      status: s.status,
      start_date: s.start_date,
      notes: s.notes,
    }));
    const { error } = await db
      .from("company_services")
      .upsert(payload as never, { onConflict: "company_id,service_id" });
    if (error) return notify.error(error);
    notify.success(`Copied ${payload.length} services`);
    onOpenChange(false);
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy service plan from another company</DialogTitle>
          <DialogDescription>Existing lines are kept unless you tick "replace".</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Searched on the API: the plan is copied from one company, so there
              is no reason to hold the register to find it. This company itself
              is left out — copying a plan onto itself is a no-op. */}
          <CompanySelect
            value={sourceId}
            onChange={setSourceId}
            placeholder="Pick a source company…"
            excludeIds={[companyId]}
          />
          <div className="flex items-center justify-between rounded-md border p-3">
            <Label>Replace existing plan</Label>
            <Switch checked={overwrite} onCheckedChange={setOverwrite} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={run} disabled={!sourceId}>
            Copy services
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkApplyDialog({
  open,
  onOpenChange,
  clientGroupId,
  sourceCompanyId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  clientGroupId: string | null;
  sourceCompanyId: string;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [overwrite, setOverwrite] = useState(false);
  const { data: companies = [] } = useQuery({
    queryKey: ["companies_in_group", clientGroupId],
    enabled: !!clientGroupId && open,
    queryFn: async () =>
      (
        await api.companies
          .list()
          .all()
          .select("id, legal_name")
          .eq("client_group_id", clientGroupId!)
          .neq("id", sourceCompanyId)
          .order("legal_name")
      ).data ?? [],
  });
  const toggle = (id: string) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setSelected(n);
  };
  const run = async () => {
    if (selected.size === 0) return;
    const { data: src } = await db
      .from("company_services")
      .select("*")
      .eq("company_id", sourceCompanyId);
    if (!src?.length) return notify.error("This company has no services to apply");
    for (const targetId of selected) {
      if (overwrite) await db.from("company_services").delete().eq("company_id", targetId);
      const payload = src.map((s) => ({
        company_id: targetId,
        service_id: s.service_id,
        billing_frequency: s.billing_frequency,
        fee: s.fee,
        invoice_description: s.invoice_description,
        recurring: s.recurring,
        auto_suggest: s.auto_suggest,
        approval_required: s.approval_required,
        status: s.status,
        start_date: s.start_date,
        notes: s.notes,
      }));
      const { error } = await db
        .from("company_services")
        .upsert(payload as never, { onConflict: "company_id,service_id" });
      if (error) {
        notify.error(error);
        return;
      }
    }
    notify.success(`Applied to ${selected.size} companies`);
    onOpenChange(false);
    setSelected(new Set());
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply this plan to companies in the same client group</DialogTitle>
          <DialogDescription>Tick companies to copy this plan into.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {companies.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No other companies in this client group.
            </p>
          ) : (
            companies.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-md border p-2">
                <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                <span className="text-sm">{c.legal_name}</span>
              </div>
            ))
          )}
          {companies.length > 0 && (
            <div className="flex items-center justify-between rounded-md border p-3 mt-3">
              <Label>Replace existing plan on each target</Label>
              <Switch checked={overwrite} onCheckedChange={setOverwrite} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={run} disabled={selected.size === 0}>
            Apply to {selected.size} companies
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverrideDialog({
  row,
  onClose,
  onSubmit,
}: {
  row: CompanyService | null;
  onClose: () => void;
  onSubmit: (fee: number, reason: string, notes: string) => void;
}) {
  const [fee, setFee] = useState<string>("");
  const [reason, setReason] = useState<string>("special_client_agreement");
  const [notes, setNotes] = useState<string>("");
  const open = !!row;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change fee</DialogTitle>
          <DialogDescription>
            The original and revised fee, reason, and your identity are recorded in the audit trail.
          </DialogDescription>
        </DialogHeader>
        {row && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Original fee: <span className="font-mono">USD {Number(row.fee).toFixed(2)}</span>
            </div>
            <div className="space-y-2">
              <Label required>Revised fee</Label>
              <Input
                type="number"
                step="0.01"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                placeholder="Enter new fee"
              />
            </div>
            <div className="space-y-2">
              <Label required>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OVERRIDE_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {REASON_LABEL[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const n = Number(fee);
              if (isNaN(n) || fee === "") return notify.error("Enter a valid fee");
              onSubmit(n, reason, notes);
              setFee("");
              setNotes("");
            }}
          >
            Record change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({
  open,
  onOpenChange,
  overrides,
  services,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  overrides: Override[];
  services: { id: string; code: string; name: string }[];
}) {
  const svcName = (id: string | null) =>
    id ? (services.find((s) => s.id === id)?.name ?? "—") : "—";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Fee override history</DialogTitle>
          <DialogDescription>All fee changes recorded for this company.</DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-y-auto">
          {overrides.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No overrides recorded.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead className="text-right">Original</TableHead>
                  <TableHead className="text-right">Revised</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overrides.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="text-xs">
                      {new Date(o.changed_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm">{svcName(o.service_id)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {Number(o.original_fee).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {Number(o.revised_fee).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {REASON_LABEL[o.reason] ?? o.reason}
                      {o.reason_notes && (
                        <div className="text-muted-foreground">{o.reason_notes}</div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
